import * as React from 'react';
import { useState } from 'react';

import type { CourseraTierSubscriptions_BillingCycle as BillingCycleType } from '__generated__/graphql-types';

import type { ButtonProps } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';
import type { ApiStatus } from '@coursera/coursera-ui';
import {
  API_BEFORE_SEND,
  API_ERROR,
  API_IN_PROGRESS,
  API_SUCCESS,
} from '@coursera/coursera-ui/lib/constants/sharedConstants';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import useSubscriptionTiersMidCycleUpgradeMutation from 'bundles/coursera-plus-common/utils/useSubscriptionTiersMidCycleUpgradeMutation';
import savedCartUtils from 'bundles/payments-common/lib/savedCartUtils';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type PropsFromCaller = {
  courseId?: string;
  s12nId?: string;
  fromTierSubscriptionId: string;
  handleUpgradeSuccessful: () => void;
  isFullWidth?: boolean;
  isStandaloneCourseraPlusUpgrade?: boolean;
  children: React.ReactNode;
  toTierBillingCycle: BillingCycleType;
  countryIsoCode?: string;
  currencyCode?: string;
};

type PropsToComponent = PropsFromCaller;

const UpgradeNowButton = ({
  children,
  courseId,
  s12nId,
  fromTierSubscriptionId,
  handleUpgradeSuccessful,
  isFullWidth = false,
  isStandaloneCourseraPlusUpgrade,
  toTierBillingCycle,
  countryIsoCode,
  currencyCode,
}: PropsToComponent) => {
  const { midCycleUpgrade } = useSubscriptionTiersMidCycleUpgradeMutation();
  const [apiStatus, setApiStatus] = useState<ApiStatus>(API_BEFORE_SEND);
  const trackingData = {
    courseId,
    s12nId,
  };

  const handleClick = () => {
    if (apiStatus === API_IN_PROGRESS || typeof midCycleUpgrade !== 'function') {
      return;
    }

    setApiStatus(API_IN_PROGRESS);

    const options = { courseId, s12nId, toTierBillingCycle, fromTierSubscriptionId, countryIsoCode, currencyCode };
    midCycleUpgrade(options)
      .then(() => {
        setApiStatus(API_SUCCESS);

        if (isStandaloneCourseraPlusUpgrade) {
          setTimeout(() => {
            handleUpgradeSuccessful();
          }, 1000);
        }

        // we are clearing any saved cart from local storage if the learner
        // first visited the checkout page before coming back to upgrade here
        // this will prevent them from seeing the persistent cart icon
        // on the global navigation bar
        savedCartUtils.reset();
        handleUpgradeSuccessful();
      })
      .catch(() => {
        setApiStatus(API_ERROR);
      });
  };

  return (
    <TrackedButton
      variant="primary"
      trackingName="ideal_upgrade_button"
      data-testid="ideal-upgrade-button"
      trackingData={trackingData}
      onClick={handleClick}
      loading={apiStatus !== API_BEFORE_SEND}
      disabled={apiStatus !== API_BEFORE_SEND}
      fullWidth={isFullWidth}
    >
      {children}
    </TrackedButton>
  );
};

export default UpgradeNowButton;
